// src/utils/authHelper.js - Versión Corregida
// Este archivo contiene funciones auxiliares para la autenticación
// con implementaciones más robustas y consistentes

/**
 * Determina si un usuario es de tipo empresa de manera consistente
 * @param {Object} user - Usuario de Supabase Auth con o sin perfil
 * @returns {boolean} - True si es empresa, false si es individual
 */
const isCompanyUser = (user) => {
  if (!user) return false;
  
  // Verificación en orden de prioridad:
  
  // 1. Comprobar campo unificado (después de getFullUserProfile)
  if (user.user_type === 'company') return true;
  
  // 2. Comprobar en metadatos de usuario (al registrar)
  if (user.user_metadata) {
    if (user.user_metadata.user_type === 'company' || 
        user.user_metadata.type === 'company') {
      return true;
    }
  }
  
  // 3. Comprobar por campos específicos de empresa
  if (user.subscription_tier || user.company_name) return true;
  
  // 4. Comprobar si existe un perfil de empresa
  if (user.company_profiles) return true;
  
  // Por defecto, asumir usuario individual
  return false;
};

/**
 * Obtiene el nivel de membresía de un usuario
 * @param {Object} user - Usuario con perfil
 * @returns {string} - Nivel de membresía: 'bronze', 'silver', 'gold'
 */
const getUserMembershipLevel = (user) => {
  if (!user) return 'bronze';
  
  // Para empresas, usar subscription_tier
  if (isCompanyUser(user)) {
    return (user.subscription_tier || 'bronze').toLowerCase();
  }
  
  // Para individuales, usar membership_level
  return (user.membership_level || 'bronze').toLowerCase();
};

/**
 * Determina la ruta de redirección según el tipo de usuario y nivel
 * @param {Object} user - Usuario con perfil
 * @returns {string} - Ruta de redirección
 */
const getRedirectPathForUser = (user) => {
  if (!user) return '/login';

  // Administrador siempre va al dashboard admin
  if (user.is_admin === true) return '/admin';

  // Si es empresa (legacy: user_type === 'company'), va al dashboard de empresa
  if (isCompanyUser(user)) return '/company';

  // Técnico (individual) con empresa activa Y "último dashboard usado" = company:
  // respetamos el último contexto elegido, estilo Instagram. Si el user cerró
  // sesión en el panel de empresa, al volver a loguear entra ahí de nuevo.
  // (Solo aplica si SSR es false — protegemos localStorage por si acaso).
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const lastDashboard = localStorage.getItem('last_dashboard');
      const activeCompanyId = localStorage.getItem('active_company_id');
      if (lastDashboard === 'company' && activeCompanyId) {
        return '/company';
      }
    }
  } catch {
    // localStorage no disponible; caemos al default
  }

  // Para usuarios individuales, depende del nivel
  const level = getUserMembershipLevel(user);

  if (level === 'gold') return '/gold-member';
  if (level === 'silver') return '/silver-member';

  // Por defecto, nivel bronze
  return '/bronze-member';
};

/**
 * Prepara los metadatos de usuario de forma consistente
 * @param {string} userType - Tipo de usuario ('individual' o 'company')
 * @returns {Object} - Objeto con metadatos preparados
 */
const prepareUserMetadata = (userType = 'individual') => {
  return {
    user_type: userType,
    type: userType, // Redundancia para mayor compatibilidad
    is_company: userType === 'company'
  };
};

/**
 * Limpia todos los indicadores de sesión
 */
const clearAllSessionIndicators = () => {
  localStorage.removeItem('avlink_user_logged_in');
  localStorage.removeItem('avlink_last_user_id');
  localStorage.removeItem('avlink_session_active');
  localStorage.removeItem('last_registered_user_type');
};

/**
 * Registra información detallada sobre el tipo de usuario para debugging
 * @param {Object} user - Usuario con o sin perfil
 */
const logUserTypeInfo = (user) => {
  if (!user) {
    console.log('⚠️ No hay usuario para mostrar info');
    return;
  }
  
  const isCompany = isCompanyUser(user);
  
  console.log('🔍 INFORMACIÓN DE TIPO DE USUARIO:');
  console.log(`- ID: ${user.id}`);
  console.log(`- Es empresa según la detección: ${isCompany ? 'SÍ' : 'NO'}`);
  console.log(`- Campo user_type: ${user.user_type || 'no definido'}`);
  
  if (user.user_metadata) {
    console.log(`- Metadatos.user_type: ${user.user_metadata.user_type || 'no definido'}`);
    console.log(`- Metadatos.type: ${user.user_metadata.type || 'no definido'}`);
  }
  
  console.log(`- Nivel de membresía: ${getUserMembershipLevel(user)}`);
  console.log(`- Es admin: ${user.is_admin ? 'SÍ' : 'NO'}`);
};

// Exportar todas las funciones
const authHelper = {
  isCompanyUser,
  getUserMembershipLevel,
  getRedirectPathForUser,
  prepareUserMetadata,
  clearAllSessionIndicators,
  logUserTypeInfo
};

export default authHelper;